import mixpanel from 'mixpanel-browser'
import variables from 'variables/variables'
import { mixpanelEndpointMap, mixpanelOrgGroupKey } from '@gap/utils/mixpanel-utils'
import { getOrgAccountStatus } from '@gap/utils/org/org-account-status'
import { VgcAnalyticsEvent } from './VgcAnalytics'
import { MindsetAnalyticsEvent } from './MindsetAnalytics'
import { MeetingAnalyticsEvent, MeetingFeedback, MeetingPlatform } from './MeetingAnalytics'
import { apiClientSilent } from 'api/trpc/trpc-client'
import { ShareAnalyticsEvent } from './ShareAnalytics'
import { AuthAnalyticsEvent, AuthAnalyticsEventOptions, AuthAnalyticsPropertyUtm } from './AuthAnalytics'
import { UserOrgDto } from '../app-services/user-org/models/UserOrgDto'
import Debug from 'reusable/utils/debug'
import { FeedbackAnalyticsEvent } from './FeedbackAnalytics'
import { SSOAnalyticsTypes } from './SSOAnalytics'
import { DocumentWorkspaceAnalyticsEvent } from './DocumentWorkspaceAnalytics'
import { OrgAnalyticsEvent } from './OrgAnalytics'
import { MessageTemplateAnalyticsEvent } from './MessageTemplateAnalytics'
import { CalendarAnalyticsEvent } from './CalendarAnalytics'
import { postgresApiClientSilent } from 'api/trpc-pg/trpc-client'
import type { CalendarProvider } from '@gap/backend/app-services/calendar/models/CalendarEnums'

export class Analytics {
   private static instance: Analytics
   private static ORG_GROUP_KEY = mixpanelOrgGroupKey

   private constructor() {
      mixpanel.init(import.meta.env.VITE_MIXPANEL_API_TOKEN, {
         persistence: 'localStorage',
         track_pageview: false, // This will be done manually on route changes
         api_host: `${variables.apiUrl}/mix`,
         api_method: 'POST',
         api_routes: mixpanelEndpointMap,
         ignore_dnt: variables.stage.startsWith('dev-')
      })
   }

   static get isInitialised() {
      return !!this.instance
   }

   static init() {
      if (this.isInitialised) {
         console.warn('Mixpanel initialised more than once')
      } else if (import.meta.env.VITE_MIXPANEL_API_TOKEN) {
         this.instance = new Analytics()
      }
   }

   /** Call this when a user loses their session or signs out */
   static reset() {
      if (!this.isInitialised) return

      mixpanel.reset()
   }

   static setUserId(id: string) {
      Debug.log.mixpanel('set user as', id)
      if (!this.isInitialised) return

      mixpanel.identify(id)
   }

   static emitRouteChange(pathName: string) {
      Debug.log.mixpanel('emit route change', pathName)
      if (!this.isInitialised) return

      mixpanel.track_pageview(
         {
            'Route path': pathName,
         },
         {
            event_name: 'Page view',
         }
      )
   }

   static setUserProperties(userProperties: Partial<AnalyticsUserProperties>) {
      Debug.log.mixpanel('set user properties', userProperties)
      if (!this.isInitialised) return

      mixpanel.people.set(userProperties)
   }

   static setOrgProperties({ orgId, ...orgProperties }: AnalyticsOrgPropertyChangeset) {
      Debug.log.mixpanel('set org properties', orgId, orgProperties)
      if (!this.isInitialised) return

      mixpanel.add_group(this.ORG_GROUP_KEY, orgId)
      mixpanel.get_group(this.ORG_GROUP_KEY, orgId).set(orgProperties)
   }

   static emitEvent<EventType extends AnalyticsEventType>(event: EventType, properties: AnalyticsEventOptions[EventType], emitOptions?: EmitOptions) {
      Debug.log.mixpanel('emit event', event, properties)
      if (!this.isInitialised) return undefined

      // Remove special property groups from the event
      if (properties && 'user' in properties && typeof properties.user === 'object') {
         this.setUserProperties(properties.user)
         delete properties.user
      }
      if (properties && 'org' in properties && typeof properties.org === 'object') {
         this.setOrgProperties(properties.org as AnalyticsOrgPropertyChangeset)
         delete properties.org
      }

      // If utm params are present, register them. UTM is used to track where a user came from(marketing)
      if (properties && 'utmParams' in properties && typeof properties.utmParams === 'object') {
         Debug.log.mixpanel('registering utm params', properties.utmParams)
         mixpanel.register(Object.fromEntries(Object.entries(properties.utmParams).filter(([key]) => key.startsWith('utm_'))))
         delete properties.utmParams
      }

      const trackedProperties = emitOptions?.orgId ? { ...properties, [this.ORG_GROUP_KEY]: [emitOptions.orgId] } : properties

      mixpanel.track(event, trackedProperties, {
         ...(emitOptions?.immediateAndAsync
            ? {
               send_immediately: true,
               transport: 'sendBeacon',
            }
            : {}),
      })
   }

   static getOrgAccountStatusFromDto(org: Pick<UserOrgDto.Info, 'tierName' | 'trialEndDate' | 'accountStatus'>) {
      return getOrgAccountStatus(org)
   }

   static async setActiveIntegrations(orgId: string, integrations: Partial<Record<ActiveIntegrations, boolean>>) {
      const activeIntegrations: ActiveIntegrations[] = []
      let connectedCalendarProviders: Promise<CalendarProvider[]> | undefined
      const isCalendarConnected = async (provider: CalendarProvider) => {
         connectedCalendarProviders ??= postgresApiClientSilent.calendar.getIntegrations.query()
            .then(calendarIntegrations => calendarIntegrations.filter(calendarIntegration => calendarIntegration.status === 'connected').map(calendarIntegration => calendarIntegration.provider))
            .catch(() => [])
         return (await connectedCalendarProviders).includes(provider)
      }

      for (const integration of Object.values(ActiveIntegrations)) {
         if (integration in integrations) {
            if (integrations[integration] === true) {
               activeIntegrations.push(integration)
            }
         } else {
            switch (integration) {
               case ActiveIntegrations.XPM:
                  if (await apiClientSilent.xpm.isConnected.query()) {
                     activeIntegrations.push(integration)
                  }
                  break
               case ActiveIntegrations.FYI:
                  if ((await apiClientSilent.fyi.getConnectionStatus.query()) === 'connected') {
                     activeIntegrations.push(integration)
                  }
                  break
               case ActiveIntegrations.GoogleCalendar:
                  if (await isCalendarConnected('google')) {
                     activeIntegrations.push(integration)
                  }
                  break
               case ActiveIntegrations.OutlookCalendar:
                  if (await isCalendarConnected('outlook')) {
                     activeIntegrations.push(integration)
                  }
                  break
            }
         }
      }

      Analytics.setOrgProperties({
         orgId,
         "Active integrations": activeIntegrations
      })
   }
}

export const AnalyticsEvent = {
   videoWatched: 'Video watched', // on boarding video?
   documentEdited: 'Document edited',

   createWorkItemLearnMoreClicked: 'Process details viewed',
   workItemCreated: 'Work item created',
   workItemCompleted: 'Work item completed',
   meetingTemplateLearnMoreClicked: 'Meeting process details viewed',
   documentTemplateCreated: 'Document template created',
   documentTemplatePublished: 'Document template published',

   proposalCreated: 'Proposal created',
   proposalSent: 'Proposal sent',
   proposalAccepted: 'Proposal accepted',

   activeIntegrations: 'Active integrations',
   processesViewed: 'Processes viewed',
   orgSettingsViewed: 'Org settings viewed',
   helpArticlesViewed: 'Help articles viewed',
   communityGroupViewed: 'Facebook community group viewed',
   entityLocked: 'Locked entity access denied',

   planUpgraded: 'Plan upgraded',

   ...FeedbackAnalyticsEvent,
   ...DocumentWorkspaceAnalyticsEvent,
   ...MindsetAnalyticsEvent,
   ...VgcAnalyticsEvent,
   ...MeetingAnalyticsEvent,
   ...ShareAnalyticsEvent,
   ...AuthAnalyticsEvent,
   ...SSOAnalyticsTypes.Events,
   ...OrgAnalyticsEvent,
   ...MessageTemplateAnalyticsEvent,
   ...CalendarAnalyticsEvent
} as const

export type AnalyticsEventType = (typeof AnalyticsEvent)[keyof typeof AnalyticsEvent]

type AnalyticsPrimativeData = string | number | boolean | Date

export type AnalyticsEventProperties = {
   [key: string]: AnalyticsPrimativeData | AnalyticsPrimativeData[] | AnalyticsOrgPropertyChangeset | Partial<AnalyticsUserProperties> | undefined
   org?: AnalyticsOrgPropertyChangeset
   user?: Partial<AnalyticsUserProperties>
   utmParams?: Partial<AuthAnalyticsPropertyUtm>
}

type AnalyticsEventOptionsProps = Record<AnalyticsEventType, AnalyticsEventProperties | undefined> & AuthAnalyticsEventOptions

/**
 * We can use this interface to define additional data to be sent with certain events.
 */
interface AnalyticsEventOptions extends AnalyticsEventOptionsProps {
   [AnalyticsEvent.videoWatched]: {
      "Video title": string
      "Percent watched": number
      "Duration watched": number
      "Total duration": number
   }
   [AnalyticsEvent.workItemCreated]: {
      "Process": string, // process name
      "Initiated from": 'Work items' | 'Getting started checklist'
   }
   [AnalyticsEvent.createWorkItemLearnMoreClicked]: {
      "Process": string // process name
   }
   [AnalyticsEvent.meetingTemplateLearnMoreClicked]: {
      "Process": string
   }
   [AnalyticsEvent.proposalCreated]: {
      "Initiated from": 'Proposals' | 'Getting started checklist' | 'Duplicate button'
   }
   [AnalyticsEvent.documentEdited]: {
      'Document type': string
   }
   [AnalyticsEvent.meetingJoined]: {
      Platform: MeetingPlatform
   }
   [AnalyticsEvent.confirmConsent]: {
      'User Email': string
   }
   [AnalyticsEvent.meetingFeedback]: {
      'Feedback': MeetingFeedback
      'User Email': string
   }
   [AnalyticsEvent.meetingOpened]: {
      'Entry point': string
      'Meeting template name': string
      'Is first meeting': boolean
      'Is demo': boolean
   }
   [AnalyticsEvent.meetingAgendaTemplateChanged]: {
      'Template source': string
      'Template name': string
   }
   [AnalyticsEvent.meetingTemplateApplied]: {
      'Template source': string
      'Template name': string
   }
   [AnalyticsEvent.meetingAgendaBlockAdded]: {
      'Block type': string
   }
   [AnalyticsEvent.meetingPreworkSent]: {
      'Template source': string
      'Template name': string
      'Shared via': string
      'Message template name': string
   }
   [AnalyticsEvent.meetingPreworkReceived]: Record<string, never>
   [AnalyticsEvent.meetingPreworkTemplateChanged]: {
      'Template source': string
      'Template name': string
   }
   [AnalyticsEvent.meetingScribeConsent]: {
      'Consent outcome': string
   }
   [AnalyticsEvent.meetingScribeInvited]: {
      'Agent name': string
   }
   [AnalyticsEvent.meetingScribeCancelled]: {
      'Agent name': string
   }
   [AnalyticsEvent.meetingScribeStarted]: {
      'Agent name': string
      'Transcription method': string
   }
   [AnalyticsEvent.meetingScribeSessionEnded]: {
      'Agent name': string
      'Duration minutes': number
      'Reason': string
      'Transcription method': string
   }
   [AnalyticsEvent.meetingDeliverableGenerated]: {
      'Agent name': string
      'Deliverable type': string
      'Generation count': number
      'Context sources': string[]
      'Is demo': boolean
      'Meeting name': string
   }
   [AnalyticsEvent.meetingDeliverableEdited]: {
      'Deliverable type': string
      'Is demo': boolean
   }
   [AnalyticsEvent.meetingDeliverableShared]: {
      'Deliverable type': string
      'Is demo': boolean
   }
   [AnalyticsEvent.meetingDeliverableEditedAfterSharing]: {
      'Deliverable type': string
      'Context sources': string[]
      'Generation count': number
      'Is demo': boolean
   }
   [AnalyticsEvent.meetingAgendaEdited]: {
      'Duration minutes': number
   }
   [AnalyticsEvent.meetingPreworkEdited]: {
      'Duration minutes': number
   }
   [AnalyticsEvent.meetingTranscriptPasted]: Record<string, never>
   [AnalyticsEvent.meetingTranscriptEdited]: Record<string, never>
   [AnalyticsEvent.meetingAiFeedbackGiven]: {
      'Sentiment': string
      'Comment': string
      'Feedback target': string
      'Agent name': string
      'Is demo': boolean
   }
   [AnalyticsEvent.meetingAgentError]: {
      'Agent name': string
      'Error type': string
   }
   [AnalyticsEvent.meetingLeft]: {
      'Meeting ID': string
      'Duration minutes': number
      'Last mode': string
      'Completion state': string[]
      'Transcript char count': number
      'Agenda char count': number
      'Notes char count': number
   }
   [AnalyticsEvent.coachReportGenerated]: {
      'Agent name': string
      'Context sources': string[]
      'Generation count': number
      'Meeting name': string
   }
   [AnalyticsEvent.coachPageOpened]: {
      'Report count': number
   }
   [AnalyticsEvent.coachReportOpened]: {
      'Initiated from': string
   }
   [AnalyticsEvent.documentTemplateCreated]: {
      "Starting point"?: string
      "Initiated from": 'Document templates' | 'Getting started checklist'
   }
   [AnalyticsEvent.documentTemplatePublished]: {
      "Document name": string
   }
   [AnalyticsEvent.workItemCompleted]: {
      "Process name": string
   }
   [AnalyticsEvent.processesViewed]: {
      "Initiated from": 'View all services' | 'Work items'
   }
   [AnalyticsEvent.orgSettingsViewed]: {
      "Initiated from": 'Profile menu'
   }
   [AnalyticsEvent.helpArticlesViewed]: {
      "Initiated from": 'Help menu'
   }
   [AnalyticsEvent.communityGroupViewed]: {
      "Initiated from": 'Help menu'
   }
   [AnalyticsEvent.mindsetOpenViaUtilityPanel]: {
      'Process name': string
   }
   [AnalyticsEvent.entityLocked]: {
      "Entity type": string
   }
   [AnalyticsEvent.vgcTacticSwitch]: {
      "Enabled": boolean
   }
   [AnalyticsEvent.vgcOpened]: {
      "Process name"?: string
      "Initiated from": 'Main menu' | 'Workspace' | 'Getting started checklist' | 'Meeting'
   }
   [AnalyticsEvent.planUpgraded]: {
      'Previous plan': string
      'New plan': string
      'Changed by': 'Gap' | 'Advisor'
   }
   [AnalyticsEvent.messageTemplateCreated]: {
      'Message template name': string
   }
   [AnalyticsEvent.messageTemplateEdited]: {
      'Message template name': string
   }
}

export const getUtmPropertiesFromQueryString = (queryString: URLSearchParams): Partial<AuthAnalyticsPropertyUtm> => {
   const utmParams = {
      utm_source: queryString.get('utm_source'),
      utm_medium: queryString.get('utm_medium'),
      utm_campaign: queryString.get('utm_campaign'),
      utm_term: queryString.get('utm_term'),
      utm_content: queryString.get('utm_content')
   }

   // Filter out null values
   return Object.fromEntries(
      Object.entries(utmParams).filter(([_, v]) => v !== null)
   ) as unknown as Partial<AuthAnalyticsPropertyUtm>
}

interface EmitOptions {
   /**
    * Will use navigator.sendBeacon to prevent an XHR request being cancelled by the user navigating away, eg for SSO auth flows
    */
   immediateAndAsync?: boolean
   /** Attributes the event to this org's group instead of the session's org. */
   orgId?: string
}

export interface AnalyticsUserProperties {
   $first_name: string
   $last_name: string
   $email: string
   'User type': AnalyticsUserType
   'User role': string
   'Authentication method': 'Email' | 'Xero SSO'
   'Registration method': 'Trial' | 'Invitation' | 'Xero Marketplace'
   'Job title': string
   'Situation': string
   'Barriers': string[]
   'Org name': string
   'Org website': string
   'Org country': string
}

/**
* Hint: Use `AnalyticsOrgPropertyChangeset<'property one' | 'etc'>` instead to always require orgId
*/
interface AnalyticsOrgProperties {
   // General info
   orgId: string
   $name: string
   'Type': string
   'Country': string
   'Website': string
   'Active integrations': ActiveIntegrations[],
   // Subscription-related info
   'Account status': string
   'Plan': string
   'Trial expiry': string | undefined
   '# of users': number
   'Company size': string | undefined
}

export enum ActiveIntegrations {
   XPM = 'XPM',
   FYI = 'FYI',
   GoogleCalendar = 'Google Calendar',
   OutlookCalendar = 'Outlook Calendar'
}

type AnalyticsOrgPropertyChangeset<T extends (keyof Omit<AnalyticsOrgProperties, 'orgId'>) | undefined = undefined> = {
   orgId: string;
} & (T extends undefined
   ? Partial<AnalyticsOrgProperties>
   : Pick<AnalyticsOrgProperties, NonNullable<T>>)

export type AnalyticsUserType = 'Gap' | 'Advisor' | 'Client'
